import { MatchUpStatusEnum, MatchUpTypeEnum, type Venue } from '@clubspark-node/clubspark-tennis-types';
import { type Dayjs } from 'dayjs';
import { type TFunction } from 'i18next';

import { type MatchParticipantStatusCategory } from 'components/dialogs/scoringDialog/typedefs/scoringTypes';
import { getClientConfig } from 'config/clients';
import { getScheduledAt } from 'functions/renderTime';
import { getSeed } from 'pdf/utils/participant';
import { type MatchUpInterface } from 'typedefs/store/scheduleTypes';
import { type EventInterface, type ParticipantInterface } from 'typedefs/store/tmxTypes';
import { todsScheduleToFormat } from 'utils/dateTimeUtils';
import { mapEventShortCode } from 'utils/eventUtils';
import { getParticipantNameWithSeedValue } from 'utils/participant';

export const formatMatchUpStatus = (matchUp: MatchUpInterface) => {
  const matchUpStatus = matchUp?.matchUpStatus;
  if (!matchUpStatus) {
    return '';
  }

  if (matchUpStatus === MatchUpStatusEnum.TO_BE_PLAYED || matchUpStatus === MatchUpStatusEnum.COMPLETED) return '';
  if (matchUp?.matchUpType === MatchUpTypeEnum.TEAM && matchUp?.matchUpStatus === MatchUpStatusEnum?.IN_PROGRESS)
    return '';

  return `${matchUpStatus.charAt(0).toUpperCase()}${matchUpStatus.slice(1, 3).toLowerCase()}`;
};

export const getEventsDivisionData = (allEvents: EventInterface[]) =>
  allEvents?.map((event) => {
    const eventDivision = event.extensions?.find((extension) => extension.name === 'division')?.value;
    return {
      eventId: event.eventId,
      division: {
        ballColour: eventDivision?.ballColour,
        eventType: event.eventType,
        familyType: eventDivision?.familyType,
        gender: eventDivision?.gender || event.gender,
        ageCategory: eventDivision?.ageCategory || event.category?.ageCategoryCode || event.category?.categoryName,
        ratingCategory: eventDivision?.ratingCategory,
        wheelchairRating: eventDivision?.wheelchairRating,
        tournamentCategory: event.tournamentCategory,
      },
    };
  });

export const getPotentialParticipantName = (
  potentialParticipants: ParticipantInterface &
    {
      participantId?: string;
      participantName?: string;
      bye?: boolean;
    }[],
) => {
  return (
    (potentialParticipants?.length < 2 ? [potentialParticipants[0], undefined] : potentialParticipants)
      ?.map((participant) => (participant?.bye || !participant ? 'Unknown' : participant.participantName))
      .join(' or ') ?? ''
  );
};

export const getParticipantName = (
  participant: ParticipantInterface & {
    bye?: boolean;
  },
  seedValue: number,
  t: TFunction,
) => {
  if (participant.participantName) return getParticipantNameWithSeedValue(participant.participantName, seedValue);
  if (participant.bye) return t('bye');
  return t('unk');
};

export const createParticipantName = (
  matchUps: MatchUpInterface[],
  currentMatchUp: MatchUpInterface,
  side: 1 | 2,
  t: TFunction,
) => {
  const mSide = currentMatchUp.sides?.[side - 1];
  const participant = mSide?.participant;
  if (!participant) return { participantName: '', seeded: false };
  const seedValue = getSeed(participant.participantId, matchUps);
  const participantName = getParticipantName(participant, seedValue, t);

  return { participantName, seeded: !!seedValue };
};

export const getNextMatchVenue = (
  matchUp: MatchUpInterface,
  tournamentVenues: Venue[],
  winnerLoser: 'winnerTo' | 'loserTo',
) => tournamentVenues?.find((venue) => venue.venueId === matchUp[winnerLoser]?.schedule?.venueId)?.venueAbbreviation;

export const getSideNames = ({
  matchUp,
  sideOneParticipantName,
  sideTwoParticipantName,
}: {
  matchUp: MatchUpInterface;
  sideOneParticipantName: string;
  sideTwoParticipantName: string;
}) => {
  if (matchUp.potentialParticipants) {
    const potentialParticipants = [...matchUp.potentialParticipants];

    const sideOneName = sideOneParticipantName || getPotentialParticipantName(potentialParticipants.shift() as any);
    const sideTwoName = sideTwoParticipantName || getPotentialParticipantName(potentialParticipants.shift() as any);

    return {
      sideOneName,
      sideTwoName,
    };
  } else {
    const sideOneName = sideOneParticipantName ?? 'Unknown';
    const sideTwoName = sideTwoParticipantName ?? 'Unknown';

    return {
      sideOneName,
      sideTwoName,
    };
  }
};

export const getPdfTemplateData = ({
  matchUps,
  tournamentInfo: {
    events,
    localTimeZone,
    statusCategories,
    tournamentEndDate,
    tournamentId,
    tournamentName,
    tournamentStartDate,
    tournamentVenues,
  },
  t,
}: {
  matchUps: MatchUpInterface[];
  tournamentInfo: {
    events: EventInterface[];
    localTimeZone: string;
    statusCategories: MatchParticipantStatusCategory[];
    tournamentEndDate: Dayjs;
    tournamentId: string;
    tournamentName: string;
    tournamentStartDate: Dayjs;
    tournamentVenues: Venue[];
  };
  t: TFunction;
}) => ({
  startDate: new Date(tournamentStartDate.toString()),
  endDate: new Date(tournamentEndDate.toString()),
  tournamentId: tournamentId,
  tournamentName: tournamentName,
  matches: matchUps.map((matchUp) => {
    const sideOneParticipant = createParticipantName(matchUps, matchUp, 1, t);
    const sideTwoParticipant = createParticipantName(matchUps, matchUp, 2, t);
    const momentScheduledAt = getScheduledAt({ matchUp: matchUp });
    const scheduledAt = momentScheduledAt || undefined;
    const venue = tournamentVenues?.find((venue) => venue.venueId === matchUp.schedule?.venueId);
    const venueName = venue?.venueName?.trim();
    const winnerNextMatchVenue = getNextMatchVenue(matchUp, tournamentVenues, 'winnerTo');
    const loserNextMatchVenue = getNextMatchVenue(matchUp, tournamentVenues, 'loserTo');
    const scheduleFormat = getClientConfig()?.formats?.dateTimeFormat;
    const winnerNextMatch = todsScheduleToFormat(matchUp.winnerTo?.schedule, localTimeZone, scheduleFormat, '');
    const loserNextMatch = todsScheduleToFormat(matchUp.loserTo?.schedule, localTimeZone, scheduleFormat, '');

    const { sideOneName, sideTwoName } = getSideNames({
      matchUp,
      sideOneParticipantName: sideOneParticipant.participantName,
      sideTwoParticipantName: sideTwoParticipant.participantName,
    });

    let matchUpStatusCodeDisplay = '';
    const matchUpStatusCode = matchUp?.matchUpStatusCodes ? matchUp.matchUpStatusCodes[0] : undefined;
    if (matchUpStatusCode) {
      const category = statusCategories.find((category) =>
        category?.subCategories?.find((subCategory) => subCategory.matchUpStatusCode === matchUpStatusCode),
      );
      matchUpStatusCodeDisplay = category
        ? category.subCategories?.find((subCategory) => subCategory.matchUpStatusCode === matchUpStatusCode)
            .matchUpStatusCodeDisplay
        : undefined;
    }

    const eventDivisionData = getEventsDivisionData(events)?.find(
      (divisionData) => divisionData.eventId === matchUp.eventId,
    )?.division;
    const eventShortCode = mapEventShortCode(eventDivisionData);

    const winningSideScore =
      matchUp.winningSide === 1 ? matchUp.score?.scoreStringSide1 : matchUp.score?.scoreStringSide2 || undefined;

    const statusCode = matchUpStatusCodeDisplay || formatMatchUpStatus(matchUp) || '';

    const sideOne = sideOneName || 'Unknown';
    const sideTwo = sideTwoName || 'Unknown';

    return {
      roundName: matchUp.roundName,
      roundNumber: matchUp.roundNumber?.toString(),
      eventName: `${eventShortCode} - ${matchUp.drawName}`,
      scheduledAt,
      sideOne,
      sideOneSeeded: sideOneParticipant.seeded,
      sideTwo,
      sideTwoSeeded: sideTwoParticipant.seeded,
      venueName,
      winnerNextMatch,
      winnerNextMatchVenue,
      loserNextMatch,
      loserNextMatchVenue,
      winningSide: matchUp.winningSide,
      winningSideScore,
      statusCode,
    };
  }),
  venueName: '',
});
